/**
 * Packaging details for an item: its weight, how it ships (polybag or box)
 * and the package's dimensions. The weight is two fields
 * (pounds and ounces, or kilograms and grams); lengths are inches or
 * centimeters, both by the workspace's units.
 */
import { useState } from "react";
import type { ItemDetail } from "@sable/api-contract";
import { defaultWeightUnit, gramsFromParts, partsFromGrams } from "@sable/domain";
import { t, tk } from "../i18n/index.ts";
import { request } from "../lib/api.ts";
import { useItemMutation } from "../lib/queries.ts";
import { useWs } from "../lib/workspace.tsx";
import { Dialog, Field, WeightFields } from "./ui.tsx";

type Kind = "polybag" | "box";

export function PackagingDialog({ item, onClose }: { item: ItemDetail; onClose: () => void }) {
  const ws = useWs();
  const lengthUnit = ws.units === "metric" ? "cm" : "in";
  const [itemW, setItemW] = useState(() => partsFromGrams(item.itemWeightGrams, ws.units));
  const [kind, setKind] = useState<Kind | "">(item.packagingType ?? "");
  const [dims, setDims] = useState(() => ({
    length: item.packageLength?.toString() ?? "",
    width: item.packageWidth?.toString() ?? "",
    height: item.packageHeight?.toString() ?? "",
  }));
  const [error, setError] = useState<string | null>(null);
  const save = useItemMutation(ws.id, item.id, (body: Record<string, unknown>) =>
    request<ItemDetail>(`/items/${item.id}`, { method: "PATCH", workspace: ws.id, body }));

  const submit = () => {
    setError(null);
    try {
      const itemGrams = gramsFromParts(itemW.major, itemW.minor, ws.units);
      const num = (s: string) => {
        const v = s.trim().replace(",", ".");
        if (v === "") return null;
        const n = Number(v);
        if (!Number.isFinite(n) || n < 0) throw new Error(t("item.packageDims"));
        return n;
      };
      const d = { length: num(dims.length), width: num(dims.width), height: num(dims.height) };
      const anyDim = d.length !== null || d.width !== null || d.height !== null;
      const unit = defaultWeightUnit(ws.units);
      save.mutate({
        itemWeightGrams: itemGrams, itemWeightUnit: itemGrams === null ? null : unit,
        packagingType: kind || null,
        packageLength: d.length, packageWidth: d.width, packageHeight: d.height,
        packageDimUnit: anyDim ? lengthUnit : null,
      }, { onSuccess: onClose });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const dim = (key: "length" | "width" | "height") => (
    <label className="unit-field">
      <input className="input num" inputMode="decimal" placeholder={t(`item.${key}`)} aria-label={`${t(`item.${key}`)} (${lengthUnit})`}
        value={dims[key]} onChange={(e) => setDims((v) => ({ ...v, [key]: e.target.value }))} />
    </label>
  );

  return (
    <Dialog
      title={t("item.packagingDetails")}
      onClose={onClose}
      footer={<>
        <button className="btn" onClick={onClose}>{t("app.cancel")}</button>
        <button className="btn btn-primary" disabled={save.isPending} onClick={submit}>
          {save.isPending ? t("app.saving") : t("item.savePackaging")}
        </button>
      </>}
    >
      <div className="stack">
        <Field label={t("intake.itemWeight")} hint={t("intake.weightHint")}>
          <WeightFields label={t("intake.itemWeight")} value={itemW} onChange={setItemW} units={ws.units} />
        </Field>
        <Field label={t("item.packagingType")}>
          <select className="select" value={kind} onChange={(e) => setKind(e.target.value as Kind | "")}>
            <option value="">—</option>
            {(["polybag", "box"] as const).map((k) => <option key={k} value={k}>{tk("packagingType", k)}</option>)}
          </select>
        </Field>
        <Field label={`${t("item.packageDims")} (${lengthUnit})`} hint={t("item.packageDimsHint")}>
          <div className="dims">
            {dim("length")}<span className="x" aria-hidden="true">×</span>{dim("width")}<span className="x" aria-hidden="true">×</span>{dim("height")}
          </div>
        </Field>
        {(error || save.isError) && <p className="form-error" role="alert">{error ?? save.error?.message}</p>}
      </div>
    </Dialog>
  );
}

/** "Box · 12 × 10 × 4 in", or null when there's nothing to say. */
export function packagingSummary(item: ItemDetail): string | null {
  const dims = [item.packageLength, item.packageWidth, item.packageHeight].filter((v): v is number => v !== null);
  const size = dims.length ? `${dims.join(" × ")} ${item.packageDimUnit ?? "in"}` : null;
  return [item.packagingType ? tk("packagingType", item.packagingType) : null, size].filter(Boolean).join(" · ") || null;
}
